import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Ticket, Clock, CheckCircle, XCircle } from "@phosphor-icons/react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { ListSkeleton } from "@/components/Skeleton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Purchases = () => {
  const { getAuthHeaders } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTicket, setExpandedTicket] = useState(null);

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/purchases`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setPurchases(response.data);
    } catch (error) {
      console.error("Erro ao carregar compras:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === "approved") return { text: "Aprovado", color: "bg-green-100 text-green-700", icon: CheckCircle };
    if (status === "pending") return { text: "Pendente", color: "bg-yellow-100 text-yellow-700", icon: Clock };
    return { text: "Cancelado", color: "bg-red-100 text-red-700", icon: XCircle };
  };

  if (loading) {
    return <div><h2 className="text-2xl sm:text-3xl font-bold text-[#042F2E] mb-6">Minhas Compras</h2><ListSkeleton count={4} /></div>;
  }

  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#042F2E] mb-2">Minhas Compras</h2>
      <p className="text-[#115E59] mb-8">Histórico de ingressos e serviços comprados</p>

      {purchases.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[#E4DFD3]">
          <Ticket size={64} weight="duotone" className="text-[#E4DFD3] mx-auto mb-4" />
          <p className="text-xl text-[#115E59]">Nenhuma compra ainda</p>
          <p className="text-sm text-[#115E59] mt-2">Seus ingressos e serviços aparecerão aqui</p>
        </div>
      ) : (
        <div className="space-y-4">
          {purchases.map((purchase, index) => {
            const badge = getStatusBadge(purchase.status);
            const BadgeIcon = badge.icon;
            const isExpanded = expandedTicket === purchase.payment_id;

            return (
              <motion.div
                key={purchase.payment_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl border border-[#E4DFD3] overflow-hidden shadow-sm"
                data-testid={`purchase-${purchase.payment_id}`}
              >
                <button
                  onClick={() => setExpandedTicket(isExpanded ? null : purchase.payment_id)}
                  className="w-full p-6 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-4">
                    <div className="bg-[#F4F1EA] p-3 rounded-xl">
                      <Ticket size={28} weight="duotone" className="text-[#FF5A26]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#042F2E]">{purchase.item_name}</h3>
                      <p className="text-sm text-[#115E59]">
                        {new Date(purchase.created_at).toLocaleDateString('pt-BR')} - R$ {purchase.amount.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold ${badge.color}`}>
                    <BadgeIcon size={16} weight="fill" />
                    {badge.text}
                  </div>
                </button>

                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    className="border-t border-[#E4DFD3] p-6 bg-[#FDFBF7]"
                  >
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-[#115E59] font-bold">Tipo:</span>
                        <span className="ml-2 text-[#042F2E]">{purchase.item_type === "package" ? "Ingresso" : "Serviço"}</span>
                      </div>
                      <div>
                        <span className="text-[#115E59] font-bold">Valor:</span>
                        <span className="ml-2 text-[#042F2E]">R$ {purchase.amount.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-[#115E59] font-bold">Data:</span>
                        <span className="ml-2 text-[#042F2E]">{new Date(purchase.created_at).toLocaleString('pt-BR')}</span>
                      </div>
                      <div>
                        <span className="text-[#115E59] font-bold">ID:</span>
                        <span className="ml-2 text-[#042F2E] font-mono text-xs">{purchase.payment_id}</span>
                      </div>
                    </div>

                    {/* Ticket QR Code if approved */}
                    {purchase.status === "approved" && purchase.ticket_qrcode && (
                      <div className="mt-6 text-center">
                        <h4 className="font-bold text-[#042F2E] mb-3">QR Code do Ingresso</h4>
                        <div className="inline-block bg-white p-4 rounded-xl border-4 border-[#FF5A26]">
                          <img
                            src={`data:image/png;base64,${purchase.ticket_qrcode}`}
                            alt="QR Code Ingresso"
                            className="w-48 h-48"
                          />
                        </div>
                        {purchase.ticket_code && (
                          <p className="mt-2 font-bold text-sm text-[#FF5A26]">{purchase.ticket_code}</p>
                        )}
                      </div>
                    )}
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Purchases;
